<template>
  <div class="wdw-forum-topic" :class="'topic-' + topicid">
    <template v-if="!topic">
      <loading loadingMessage="Bereich wird geladen..." />
    </template>

    <template v-else>
      <div class="topic controls top-controls w-100 align-self-center">
        <create-thought-button :topic="topic" @create-thought="emitCreateThought" />
      </div>

      <template v-if="loadingThoughts">
        <div class="forum-list-skeleton" v-for="n in rows" v-bind:key="n">
          <Skeleton width="100%" height="3rem" />
        </div>
      </template>
      <template v-else-if="totalThoughtsCount == 0">
        <div class="no-thoughts-found fst-italic">
          <p>Noch keine Themen vorhanden, erstellen Sie das erste Thema!</p>
        </div>
      </template>

      <template v-else>
        <div class="thought-list" v-if="thoughts.length > 0">
          <thought-in-list
            v-for="thought in thoughts"
            v-bind:key="thought.id"
            :thought="thought"
            @show-thought="openThought(thought)"
          />
        </div>
      </template>
    </template>

    <!-- leave this outside of "else" template to keep Paginator instance up -->
    <div class="d-flex flex-sm-column flex-md-row">
      <div v-if="topic" class="topic controls bottom-controls align-self-center">
        <create-thought-button :topic="topic" @create-thought="emitCreateThought" />
      </div>
      <div v-if="totalThoughtsCount > 0" class="flex-md-grow-1">
        <Paginator :rows="rows" :totalRecords="totalThoughtsCount" @page="onPageChange($event)" />
      </div>
    </div>
  </div>
</template>

<script>
import { onBeforeMount, ref, watch, computed, toRef } from 'vue';

// stores
import { userStore } from '@/store/user-store';
import { projectStore } from '@/store/project-store';

// components
import Skeleton from 'primevue/skeleton';
import Paginator from 'primevue/paginator';
import Loading from '@/components/general/Loading.vue';
import ThoughtInList from '@/components/forum/elements/ThoughtInList.vue';
import CreateThoughtButton from '@/components/forum/elements/CreateThoughtButton.vue';

// repositories
import { RepositoryFactory } from '@/api/repositoryFactory';
const ThoughtRepository = RepositoryFactory.get('thought');

export default {
  name: 'forum-single-topic',
  props: {
    topicid: {
      type: Number,
      required: true,
    },
  },
  emit: ['show-thought', 'create-thought', 'loaded-topic-title', 'show-topics'],

  setup(props, context) {
    const topicid = toRef(props, 'topicid');
    const loadingThoughts = ref(true);
    const rows = ref(10);
    const currentPage = ref(1);
    const topic = ref(null);
    const thoughts = ref([]);
    const pager = ref({});

    //
    //-----------------COMPUTED------------------
    //

    const totalThoughtsCount = computed(() => {
      return pager.value.total_entries;
    });

    //
    //-----------------METHODS------------------
    //

    function loadThoughts(pageNummber) {
      loadingThoughts.value = true;
      currentPage.value = pageNummber;
      const params = {
        topicid: topicid.value,
        rows: rows.value,
        page: pageNummber,
        fieldset: 'all',
        sort: '-last_comment',
      };
      ThoughtRepository.get(params)
        .then(result => {
          if (result.data) {
            thoughts.value = result.data;
            pager.value = result.pager;
          } else {
            console.log('error');
          }
          loadingThoughts.value = false;
        })
        .catch(error => {
          console.log(error);
          loadingThoughts.value = false;
        });
    }
    function reloadThoughts() {
      loadThoughts(currentPage.value);
    }

    function onPageChange(event) {
      loadThoughts(event.page + 1);
    }

    function openThought(thought) {
      context.emit('show-thought', {
        thoughtid: thought.id,
        name: thought.title,
      });
    }

    function emitCreateThought(params) {
      context.emit('create-thought', params);
    }

    function initTopic(refetch = false) {
      // reset paging and other data
      thoughts.value = [];
      if (!refetch) {
        pager.value = {};
      }

      // fetch topic
      projectStore
        .fetchTopic(topicid.value, refetch)
        .then(resp => {
          // check if we're allowed to access topic, in case of restricted_access
          if (resp.restrict_public_access == 0 || resp.restricted_access == 'allowed') {
            // assign project config
            if (resp.project_config && 'thought_list_rows' in resp.project_config) {
              rows.value = parseInt(resp.project_config.thought_list_rows);
            }

            // load thoughts
            if (refetch) {
              reloadThoughts();
            } else {
              loadThoughts(1);
            }
            topic.value = resp;
            context.emit('loaded-topic-title', resp.id, resp.title);
          } else {
            // not allowed, reroute back to topics
            context.emit('show-topics');
          }
        })
        .catch(err => {
          console.log(err);
          // error, reroute back to topics
          context.emit('show-topics');
        });
    }

    // initialization
    onBeforeMount(() => {
      initTopic();
    });

    //
    //-----------------WATCHERS------------------
    //

    watch(topicid, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        initTopic();
      }
    });
    watch(
      () => userStore.getState().user,
      (newUser, oldUser) => {
        // if logged in user changes make sure to refetch topic & thoughts
        if ((newUser == null && oldUser) || (newUser && oldUser == null) || newUser?.id != oldUser?.id) {
          const refetch = true;
          initTopic(refetch);
        }
      }
    );

    return {
      // REFS
      pager,
      rows,
      thoughts,
      topic,
      loadingThoughts,

      // COMPUTED
      totalThoughtsCount,

      // METHODS
      onPageChange,
      openThought,
      emitCreateThought,
    };
  },

  components: {
    Paginator,
    Loading,
    Skeleton,
    ThoughtInList,
    CreateThoughtButton,
  },
};
</script>

<style scoped lang="scss">
.thought-list {
  border: solid 1px #eee;
  border-top: none;
}
.forum-list-skeleton,
.no-thoughts-found {
  padding: 10px;
}
.controls {
  padding: 0.5rem;
}
.no-thoughts-found {
  border: solid 1px #eee;
}
</style>
